import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ContactSelect from "./ContactSelect";

function todayStartInput(timeZone) {
  if (timeZone && timeZone !== "local") {
    try {
      const parts = new Intl.DateTimeFormat("en", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(new Date());
      const get = (type) => parts.find((part) => part.type === type)?.value;
      return `${get("year")}-${get("month")}-${get("day")}T00:00`;
    } catch {
      // Si la zona no es válida, usamos la fecha local del navegador.
    }
  }
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00`;
}

function isBeforeToday(value, timeZone) {
  return !!value && value.slice(0, 10) < todayStartInput(timeZone).slice(0, 10);
}

function servicePrice(service) {
  const value = service?.price ?? service?.base_price;
  return value === "" || value == null ? null : Number(value);
}

function serviceOptionLabel(service) {
  const price = servicePrice(service);
  const duration = service?.default_duration_min
    ? ` - ${service.default_duration_min} min`
    : "";
  const amount = Number.isFinite(price) ? ` - $${price.toFixed(2)}` : "";
  return `${service.name}${duration}${amount}`;
}

export default function AppointmentFormModal({
  open,
  mode = "create", // "create" | "edit"
  initialData,
  config,
  onClose,
  onSubmit,
  submitting = false,
}) {
  const [form, setForm] = useState({
    subject_contact_id: "",
    service_id: "",
    location_id: "",
    resource_id: "",
    reason_id: "",
    status: "pending_approval",
    start_at: "",
    end_at: "",
    timezone: "",
    total_amount: "",
    notes: "",
  });
  const [validationError, setValidationError] = useState("");

  const parseToInputValue = (v) => {
    if (!v && v !== 0) return "";
    const formatDate = (d) => {
      if (!d || isNaN(d)) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    if (v instanceof Date && !isNaN(v)) return formatDate(v);
    if (typeof v === "number") {
      const d = new Date(v);
      return !isNaN(d) ? formatDate(d) : "";
    }
    if (typeof v === "string") {
      if (v.includes("T")) return v.replace(" ", "T").slice(0, 16);
      const d = new Date(v.replace(" ", "T"));
      return !isNaN(d) ? formatDate(d) : "";
    }
    return "";
  };

  useEffect(() => {
    if (!open) return;
    setValidationError("");

    if (mode === "edit" && initialData) {
      setForm({
        subject_contact_id: initialData.subject_contact_id || "",
        service_id: initialData.service_id || "",
        location_id: initialData.location_id || "",
        resource_id: initialData.resources?.[0]?.resource_id || "",
        reason_id: initialData.reason_id || "",
        status: initialData.status || "pending_approval",
        start_at: parseToInputValue(initialData.start_at ?? initialData.start),
        end_at: parseToInputValue(initialData.end_at ?? initialData.end),
        timezone: initialData.timezone || initialData.location_timezone || "",
        total_amount: initialData.total_amount ?? "",
        notes: initialData.notes || "",
      });
    } else if (mode === "create" && initialData) {
      setForm((prev) => ({
        ...prev,
        subject_contact_id: initialData.subject_contact_id || "",
        service_id: initialData.service_id || "",
        location_id: initialData.location_id || "",
        resource_id: initialData.resource_id || "",
        reason_id: initialData.reason_id || "",
        status: "pending_approval",
        start_at: parseToInputValue(initialData.start ?? initialData.start_at),
        end_at: parseToInputValue(initialData.end ?? initialData.end_at),
        timezone: initialData.timezone || "",
      }));
    } else {
      setForm({
        subject_contact_id: "",
        service_id: "",
        location_id: "",
        resource_id: "",
        reason_id: "",
        status: "pending_approval",
        start_at: "",
        end_at: "",
        timezone: "",
        total_amount: "",
        notes: "",
      });
    }
  }, [open, mode, initialData]);

  const services = Array.isArray(config?.services) ? config.services : [];
  const locations = Array.isArray(config?.locations) ? config.locations : [];
  const resources = Array.isArray(config?.resources) ? config.resources : [];
  const reasons = Array.isArray(config?.reasons) ? config.reasons : [];
  const selectedLocation = locations.find((l) => Number(l.id) === Number(form.location_id));
  const effectiveTimezone =
    selectedLocation?.timezone ||
    form.timezone ||
    config?.settings?.timezone ||
    "America/Guayaquil";

  if (!open) return null;

  const applyServiceDefaults = (next, serviceId) => {
    const service = services.find((s) => Number(s.id) === Number(serviceId));
    if (!service) {
      next.total_amount = "";
      return next;
    }

    const duration = Number(service.default_duration_min || 0);
    if (next.start_at && duration > 0) {
      const start = new Date(next.start_at);
      if (!isNaN(start)) {
        next.end_at = parseToInputValue(
          new Date(start.getTime() + duration * 60000)
        );
      }
    }

    const price = servicePrice(service);
    if (Number.isFinite(price)) {
      next.total_amount = String(price);
    }

    return next;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValidationError("");
    setForm((prev) => {
      let next = { ...prev, [name]: value };
      if (name === "service_id") next = applyServiceDefaults(next, value);
      if (name === "start_at" && next.service_id) {
        next = applyServiceDefaults(next, next.service_id);
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!onSubmit) return;
    if (mode === "create" && isBeforeToday(form.start_at, effectiveTimezone)) {
      setValidationError("No se puede crear una cita en una fecha anterior a hoy.");
      return;
    }
    if (form.start_at && form.end_at && form.end_at <= form.start_at) {
      setValidationError("La fecha/hora de fin debe ser posterior al inicio.");
      return;
    }
    const timezone =
      effectiveTimezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "America/Guayaquil";
    await onSubmit({
      ...form,
      subject_contact_id: form.subject_contact_id ? Number(form.subject_contact_id) : null,
      service_id: form.service_id ? Number(form.service_id) : null,
      location_id: form.location_id ? Number(form.location_id) : null,
      resource_id: form.resource_id ? Number(form.resource_id) : null,
      reason_id: form.reason_id ? Number(form.reason_id) : null,
      timezone,
      total_amount: form.total_amount === "" ? undefined : Number(form.total_amount),
    });
  };

  const setField = (name, value) => {
    setValidationError("");
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const minDateTime = todayStartInput(effectiveTimezone);

  return (
    // Fondo oscuro
    <div className="fixed inset-0 z-50 flex">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden
      />

      {/* Slide-over panel (desde la derecha) */}
      <aside
        className="appointment-drawer-panel relative ml-auto w-full max-w-[420px] h-full bg-[#020817] border-l border-gray-800 shadow-2xl transform transition-transform duration-300"
        role="dialog"
        aria-modal="true"
      >
        <div className="appointment-drawer-header flex items-center justify-between">
          <h3 className="text-sm font-semibold">
            {mode === "create" ? "Nueva cita" : "Editar cita"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="appointment-drawer-form">
          <div className="appointment-drawer-body space-y-3">
            <div>
              <ContactSelect
                value={form.subject_contact_id || ""}
                onChange={(value) => setField("subject_contact_id", value || "")}
              />
            </div>

            <div>
              <label className="block text-[9px] text-gray-400 mb-1">
                Servicio
              </label>
              <select
                name="service_id"
                value={form.service_id}
                onChange={handleChange}
                className="w-full px-2 py-2 text-sm rounded bg-[#020817] border border-gray-700 focus:outline-none focus:border-[color:var(--accent,#3b82f6)]"
              >
                <option value="">Sin servicio</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {serviceOptionLabel(s)}
                  </option>
                ))}
              </select>
              <div className="appointment-field-hint">
                El precio sale del maestro Citas &gt; Servicios.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[9px] text-gray-400 mb-1">Sede</label>
                <select
                  name="location_id"
                  value={form.location_id}
                  onChange={handleChange}
                  className="w-full px-2 py-2 text-sm rounded bg-[#020817] border border-gray-700 focus:outline-none focus:border-[color:var(--accent,#3b82f6)]"
                >
                  <option value="">Sin sede</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[9px] text-gray-400 mb-1">Recurso</label>
                <select
                  name="resource_id"
                  value={form.resource_id}
                  onChange={handleChange}
                  className="w-full px-2 py-2 text-sm rounded bg-[#020817] border border-gray-700 focus:outline-none focus:border-[color:var(--accent,#3b82f6)]"
                >
                  <option value="">Sin recurso</option>
                  {resources.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[9px] text-gray-400 mb-1">Motivo</label>
              <select
                name="reason_id"
                value={form.reason_id}
                onChange={handleChange}
                className="w-full px-2 py-2 text-sm rounded bg-[#020817] border border-gray-700 focus:outline-none focus:border-[color:var(--accent,#3b82f6)]"
              >
                <option value="">Sin motivo</option>
                {reasons.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[9px] text-gray-400 mb-1">Inicio</label>
                <input
                type="datetime-local"
                name="start_at"
                value={form.start_at}
                onChange={handleChange}
                min={mode === "create" ? minDateTime : undefined}
                required
                  className="w-full px-2 py-2 text-sm rounded bg-[#020817] border border-gray-700 focus:outline-none focus:border-[color:var(--accent,#3b82f6)]"
                />
              </div>
              <div>
                <label className="block text-[9px] text-gray-400 mb-1">Fin</label>
                <input
                  type="datetime-local"
                name="end_at"
                value={form.end_at}
                onChange={handleChange}
                min={form.start_at || (mode === "create" ? minDateTime : undefined)}
                className="w-full px-2 py-2 text-sm rounded bg-[#020817] border border-gray-700 focus:outline-none focus:border-[color:var(--accent,#3b82f6)]"
              />
              </div>
            </div>

            <div>
              <label className="block text-[9px] text-gray-400 mb-1">Estado</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className="w-full px-2 py-2 text-sm rounded bg-[#020817] border border-gray-700 focus:outline-none focus:border-[color:var(--accent,#3b82f6)]"
              >
                <option value="pending_approval">Pendiente aprobación</option>
                <option value="confirmed">Confirmada</option>
                <option value="canceled">Cancelada</option>
                <option value="completed">Completada</option>
              </select>
            </div>

            <div>
              <label className="block text-[9px] text-gray-400 mb-1">
                Total USD
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                name="total_amount"
                value={form.total_amount}
                readOnly
                className="w-full px-2 py-2 text-sm rounded bg-[#020817] border border-gray-700 opacity-80 cursor-not-allowed"
              />
              <div className="appointment-field-hint">
                Se llena automáticamente al elegir servicio. Para cambiarlo, edita el precio en Citas &gt; Servicios.
              </div>
            </div>

            <div>
              <label className="block text-[9px] text-gray-400 mb-1">Notas</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={4}
                className="w-full px-2 py-2 text-sm rounded bg-[#020817] border border-gray-700 focus:outline-none focus:border-[color:var(--accent,#3b82f6)] resize-none"
              />
            </div>
            {validationError && (
              <div className="appointment-field-error border border-red-400/40 rounded p-2">
                {validationError}
              </div>
            )}
          </div>

          <div className="appointment-drawer-footer flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm rounded-md border border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm rounded-md bg-[color:var(--accent,#3b82f6)] text-white hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? (mode === "create" ? "Creando..." : "Guardando...") : (mode === "create" ? "Crear cita" : "Guardar cambios")}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}

AppointmentFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]),
  initialData: PropTypes.object,
  config: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};
